import { FC, MouseEvent, useState } from 'react';
import { isSameDay } from 'date-fns';
import { DayPicker, DateRange } from 'react-day-picker';
import { ru } from 'react-day-picker/locale';

import { useMedia } from '../../../useMedia';
import dayPickerClassNames from '../../model/dayPickerClassNames';
import { DateRangePickerRangeProps } from '../../model/DateRangePickerProps';
import formatRange from '../../model/formatRange';
import getCalendarWidth from '../../model/getCalendarWidth';
import { parseDateRangeInput } from '../../model/parseDateInput';
import { DESKTOP_QUERY } from '../../model/query';
import useDisabledMatchers from '../../model/useDisabledMatchers';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import NavChevron from '../NavChevron/NavChevron';
import PickerShell from '../PickerShell/PickerShell';
import Trigger, { CommitTextSource } from '../Trigger/Trigger';

import styles from '../DateRangePicker.module.css';

export type RangeDatePickerProps = Omit<DateRangePickerRangeProps, 'mode'>;

/** Выбор диапазона дат на react-day-picker. */
const RangeDatePicker: FC<RangeDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  numberOfMonths,
  placeholder = 'Выберите период',
  isDisabled = false,
  isClearable = true,
  className,
}) => {
  const isDesktop = useMedia(DESKTOP_QUERY);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(value?.from ?? new Date());
  const resolvedDisabled = useDisabledMatchers(minDate, maxDate);
  const resolvedNumberOfMonths = numberOfMonths ?? (isDesktop ? 2 : 1);

  const handleSelect = (range: DateRange | undefined, triggerDate: Date) => {
    if (value?.from && value.to) {
      onChange?.({ from: triggerDate, to: undefined });
      return;
    }

    const wasAwaitingRangeEnd = Boolean(value?.from) && !value?.to;
    const isFreshSingleDayClick = range?.from && range?.to && isSameDay(range.from, range.to);

    if (!wasAwaitingRangeEnd && isFreshSingleDayClick) {
      onChange?.({ from: range.from, to: undefined });
      return;
    }

    onChange?.(range);

    if (range?.from && range?.to) {
      setOpen(false);
    }
  };

  const handleClear = (event: MouseEvent) => {
    event.stopPropagation();
    onChange?.(undefined);
  };

  const handleCommitText = (text: string, source: CommitTextSource) => {
    if (text.trim() === '') {
      onChange?.(undefined);
      return;
    }

    const parsed = parseDateRangeInput(text);

    if (!parsed) {
      return;
    }

    if (minDate && parsed.from < minDate) {
      return;
    }

    if (maxDate && (parsed.to ?? parsed.from) > maxDate) {
      return;
    }

    onChange?.(parsed);
    setMonth(parsed.from);

    if (parsed.to && source === 'enter') {
      setOpen(false);
    }
  };

  const trigger = (
    <Trigger
      displayText={formatRange(value)}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={isClearable}
      allowTyping={isDesktop}
      className={className}
      onOpen={() => setOpen(true)}
      onIconClick={() => setOpen((previousOpen) => !previousOpen)}
      onClear={handleClear}
      onCommitText={handleCommitText}
    />
  );

  const calendar = (
    <div
      className={styles.calendarContainer}
      style={isDesktop ? { width: getCalendarWidth(resolvedNumberOfMonths) } : undefined}>
      <DayPicker
        mode="range"
        locale={ru}
        selected={value}
        onSelect={handleSelect}
        month={month}
        onMonthChange={setMonth}
        numberOfMonths={resolvedNumberOfMonths}
        disabled={resolvedDisabled}
        captionLayout="dropdown"
        startMonth={minDate}
        endMonth={maxDate}
        navLayout="around"
        className={styles.dayPicker}
        classNames={dayPickerClassNames}
        components={{ Chevron: NavChevron, Dropdown: DropdownSelect }}
      />
    </div>
  );

  return (
    <PickerShell
      trigger={trigger}
      calendar={calendar}
      open={open}
      onOpenChange={setOpen}
    />
  );
};

export default RangeDatePicker;
